import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import {
  FaPlus, FaDollarSign, FaArrowUp, FaPercent, FaBuilding,
  FaExclamationTriangle, FaTrash, FaChevronDown, FaChevronUp, FaBullhorn
} from 'react-icons/fa';
import EmailVerifyModal from './EmailVerifyModal';
import './Sidebar.css';
import './Navbar.css';
import './Home.css';
import './EmailVerifyModal.css';
import LoadingScreen from './LoadingScreen';
import Tutorial from './Tutorial';

const BASE_URL = 'https://api.rside.app';

function Home() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [pmId, setPmId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [initials, setInitials] = useState('');
  const [loading, setLoading] = useState(true);

  const [occupancyData, setOccupancyData] = useState({ occupied: 0, total: 0 });
  const [totalIncome, setTotalIncome] = useState({ current: 0, lastMonth: 0 });
  const [totalExpenses, setTotalExpenses] = useState({ current: 0, lastMonth: 0 });
  const [todos, setTodos] = useState([]);
  const [todoPage, setTodoPage] = useState(0);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [propertyStats, setPropertyStats] = useState({ total: 0, units: 0, vacant: 0, occupied: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [monthlyIncomeExpense, setMonthlyIncomeExpense] = useState([]);
  const [miniTooltip, setMiniTooltip] = useState(null);
  const [barTooltip, setBarTooltip] = useState(null);
  const [allActivities, setAllActivities] = useState([]);
  const [vacantUnitsList, setVacantUnitsList] = useState([]);
  const [expenseCategoryList, setExpenseCategoryList] = useState([]);
  const [activeModal, setActiveModal] = useState(null);
  const [ytdIncome, setYtdIncome] = useState(0);
  const [ytdExpenses, setYtdExpenses] = useState(0);
  const [incomeByMonth, setIncomeByMonth] = useState([]);
  const [incomeByProperty, setIncomeByProperty] = useState([]);
  const [cashFlowByMonth, setCashFlowByMonth] = useState([]);
  const [ytdCashFlow, setYtdCashFlow] = useState(0);
  const [workOrders, setWorkOrders] = useState([]);
  const [woSummary, setWoSummary] = useState(null);
  const [incomeMonthOpen, setIncomeMonthOpen] = useState(false);
  const [incomePropOpen, setIncomePropOpen] = useState(false);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [reminderText, setReminderText] = useState('');
  const [reminderDueDate, setReminderDueDate] = useState('');
  const [reminderSubmitting, setReminderSubmitting] = useState(false);

  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [annTitle, setAnnTitle] = useState('');
  const [annMessage, setAnnMessage] = useState('');
  const [annExpDays, setAnnExpDays] = useState(30);
  const [annDoors, setAnnDoors] = useState([]);
  const [annSelected, setAnnSelected] = useState(new Set());
  const [annDoorsLoading, setAnnDoorsLoading] = useState(false);
  const [annSubmitting, setAnnSubmitting] = useState(false);
  const [annSuccess, setAnnSuccess] = useState(false);
  const [annError, setAnnError] = useState('');

  // Onboarding wizard for new managers
  const [onboardStep, setOnboardStep] = useState(0);
  // 0=none, 1=bank question, 2=bank UI, 3=payment question, 4=payment UI
  const [obBankConnecting, setObBankConnecting] = useState(false);
  const [showVerifyModal, setShowVerifyModal]   = useState(false);
  const [obBankStatus, setObBankStatus] = useState(null);
  const [obBankMsg, setObBankMsg] = useState('');
  const [obStripeLoading, setObStripeLoading] = useState(false);
  const [obStripeOpened, setObStripeOpened] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const plaidReady = useRef(false);

  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const monthName = now.toLocaleString('default', { month: 'long' });

  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (!user) return navigate('/login');
      try {
        setUserEmail(user.email);
        const [nameRes, accountRes] = await Promise.all([
          fetch(`${BASE_URL}/PropertyManagerName?email=${user.email}`),
          fetch(`${BASE_URL}/MyAccount?Email=${encodeURIComponent(user.email)}`),
        ]);
        const data = await nameRes.json();
        setUserData(data);
        if (accountRes.ok) {
          const accountData = await accountRes.json();
          setPmId(accountData.id || accountData.PM_ID || accountData.pm_id || null);
        }
        const parts = data.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));
        await fetchDashboardData(user.email, data.Full_Name);
        if (localStorage.getItem('isNewManager') === 'true') {
          setOnboardStep(1);
        }
      } catch (err) {
        console.error('Error loading home data:', err);
      } finally {
        setLoading(false);
      }
    };
    const unsub = auth.onAuthStateChanged((u) => u ? fetchUserData() : navigate('/login'));
    return unsub;
  }, [navigate]);

  // Load Plaid SDK for onboarding bank connection
  useEffect(() => {
    if (document.getElementById('plaid-link-sdk')) { plaidReady.current = true; return; }
    const s = document.createElement('script');
    s.id = 'plaid-link-sdk';
    s.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    s.onload = () => { plaidReady.current = true; };
    document.head.appendChild(s);
  }, []);

  const fetchDashboardData = async (email, fullName) => {
    try {
      // Occupancy
      try {
        const response = await fetch(
          `${BASE_URL}/OccupancyByPM?email=${encodeURIComponent(email)}`
        );

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const data = await response.json();

        console.log('Occupancy API Response:', data);

        const occupied = Number(data.occupied_units) || 0;
        const total = Number(data.total_units) || 0;
        const vacant = total - occupied;

        setOccupancyData({
          occupied,
          total
        });

        setPropertyStats(prev => ({
          ...prev,
          units: total,
          occupied,
          vacant
        }));

      } catch (error) {
        console.error('Error fetching occupancy:', error);
        setOccupancyData({ occupied: 0, total: 0 });
      }

      // Vacant units (for occupancy modal)
      try {
        if (fullName) {
          const propRes = await fetch(
            `${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(fullName)}&Email=${encodeURIComponent(email)}`
          );
          if (propRes.ok) {
            const propData = await propRes.json();
            const vacants = (propData.Properties || [])
              .filter((p) => {
                const status = (p.Status || '')
                  .toString()
                  .trim()
                  .toLowerCase();

                // include:
                // - "vacant"
                // - null / empty status
                // BUT exclude occupied
                return status !== 'occupied';
              })
              .map((p) => ({
                id: p.Door_id || p.Property_ID,

                name: p.Property_Name || 'Unknown Property',

                unit: p.Unit_Number || '',

                address: p.Street_Address || '',

                status: p.Status || 'Vacant',

                units: p.Num_Of_Units || 0,

                rent: 0
              }));

            setVacantUnitsList(vacants);
          }
        }
      } catch (err) { console.error('Error fetching vacant units:', err); }

      // Income
      try {
        const incomeRes = await fetch(
          `${BASE_URL}/TotalIncomeCurrentMonthByPM?email=${encodeURIComponent(email)}&month=${month}&year=${year}`
        );
        if (incomeRes.ok) {
          const d = await incomeRes.json();
          setTotalIncome({ current: toNumber(d.current_month_income), lastMonth: toNumber(d.previous_month_income) });
        }
      } catch (err) { console.error('Error fetching income:', err); }

      // Expenses
      try {
        const expRes = await fetch(
          `${BASE_URL}/TotalExpensesCurrentMonthByPM?email=${encodeURIComponent(email)}&month=${month}&year=${year}`
        );
        if (expRes.ok) {
          const d = await expRes.json();
          setTotalExpenses({ current: toNumber(d.current_month_expenses), lastMonth: toNumber(d.previous_month_expenses) });
        }
      } catch (err) { console.error('Error fetching expenses:', err); }

      // =========================
      // YTD INCOME DATA
      // =========================
      try {
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();

        const rentRes = await fetch(
          `${BASE_URL}/ListRentPayments?pm_email=${encodeURIComponent(email)}&start_month=1&end_month=${currentMonth}&start_year=${currentYear}&end_year=${currentYear}`
        );

        if (rentRes.ok) {
          const data = await rentRes.json();

          const rents = data.items || data.rent || [];

          // TOTAL YTD INCOME
          const totalIncome = rents.reduce(
            (sum, item) => sum + Number(item.Amount || 0),
            0
          );

          setYtdIncome(totalIncome);

          // =========================
          // INCOME BY MONTH
          // =========================
          const monthMap = {};

          const monthOrder = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
          ];

          rents.forEach((item) => {
            // Skip rent forgiveness — it's a write-off, not received cash
            if (item.item_type === 'rent_forgiveness' || item.Category === 'RentForgiveness') return;
            // Paydate is null for forgiveness items; use timestamp as fallback
            const payDate = item.Paydate || item.timestamp;

            if (!payDate) return;

            const dateObj = new Date(payDate);

            if (isNaN(dateObj.getTime())) return;

            const month = dateObj.toLocaleString('default', {
              month: 'long'
            });

            if (!monthMap[month]) {
              monthMap[month] = 0;
            }

            monthMap[month] += Number(item.Amount || 0);
          });

          const monthData = Object.entries(monthMap)
            .map(([month, total]) => ({
              month,
              total
            }))
            .sort(
              (a, b) =>
                monthOrder.indexOf(a.month) -
                monthOrder.indexOf(b.month)
            );

          setIncomeByMonth(monthData);

          // =========================
          // INCOME BY PROPERTY
          // =========================
          const propertyMap = {};

          rents.forEach((item) => {
            const property = item.Property_Name || 'Unknown Property';

            if (!propertyMap[property]) {
              propertyMap[property] = 0;
            }

            propertyMap[property] += Number(item.Amount || 0);
          });

          const propertyData = Object.entries(propertyMap).map(([property, total]) => ({
            property,
            total
          }));

          setIncomeByProperty(propertyData);
        }

      } catch (err) {
        console.error('Error fetching YTD income:', err);
      }

      // =========================
      // YTD CASH FLOW (SINGLE SOURCE OF TRUTH)
      // =========================
      try {
        const res = await fetch(
          `${BASE_URL}/HomeChartCashFlow?pm_email=${encodeURIComponent(email)}&range=ytd`
        );

        if (res.ok) {
          const d = await res.json();
          const labels = d.labels || [];
          const values = d.values || [];
          const currentYear = new Date().getFullYear();
          const currentMonthIndex = new Date().getMonth(); // 0-based

          // Map API response to month → value
          const monthMap = {};
          labels.forEach((label, i) => {
            const [year, month] = label.split('-').map(Number);
            if (year === currentYear) monthMap[month - 1] = Number(values[i] || 0);
          });

          // Fill months up to current month, but only include non-zero
          const monthOrder = [
            'January','February','March','April','May','June',
            'July','August','September','October','November','December'
          ];

          const fullData = [];
          for (let m = 0; m <= currentMonthIndex; m++) {
            const val = monthMap[m] || 0;
            if (val !== 0) {  // <-- skip months with 0
              fullData.push({
                label: `${monthOrder[m]}, ${currentYear}`,
                value: val,
                monthIndex: m
              });
            }
          }

          // Update state
          setCashFlowByMonth(fullData);
          setCashFlowData(fullData.map(d => ({
            month: d.label,
            cashFlow: d.value,
            isPositive: d.value >= 0
          })));
          setYtdCashFlow(fullData.reduce((sum, d) => sum + d.value, 0));
        }
      } catch (err) {
        console.error('Error fetching YTD cash flow:', err);
      }

      // TODOS
      const allTodos = [];

      try {
        // 1️⃣ Past Due Rents (only if past_due > 0)
        const pastDueRes = await fetch(`${BASE_URL}/GetPastDueRentsByPM?pm_email=${encodeURIComponent(email)}`);
        if (pastDueRes.ok) {
          const data = await pastDueRes.json();
          (data.past_due_rents || []).forEach((r, i) => {
            if (Number(r.past_due) > 0) {
              allTodos.push({
                id: `pastdue-${r.door_id}-${Date.now()}`,
                title: 'Past Due Rent',
                subtitle: r.door_address || 'Unknown Property',
                amount: `$${Number(r.past_due).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                type: 'pastdue',
                dueDate: null,
                completed: false
              });
            }
          });
        }
      } catch (err) { console.error('Error fetching past due rents:', err); }

      try {
        // 2️⃣ Manual Reminders
        const remRes = await fetch(`${BASE_URL}/GetReminders?Email=${encodeURIComponent(email)}`);
        if (remRes.ok) {
          const data = await remRes.json();
          (data.reminders || []).forEach((r, i) => {
            allTodos.push({
              id: `rem-${i}-${Date.now()}`,
              title: 'Reminder',
              subtitle: r.reminder_text,
              type: 'manual',
              dueDate: r.Due_Date,
              completed: false
            });
          });
        }
      } catch (err) { console.error('Error fetching reminders:', err); }

      try {
        // 3️⃣ Insurance Expiring Soon — show within 60 days (and after)
        const insRes = await fetch(`${BASE_URL}/InsuranceExpiringSoon?pm_email=${encodeURIComponent(email)}`);
        if (insRes.ok) {
          const data = await insRes.json();
          (data.properties || []).forEach((p, i) => {
            const expDate = p.Insurance_End ?? null;
            if (expDate) {
              const now = new Date(); now.setHours(0,0,0,0);
              const parts = String(expDate).split('T')[0].split('-');
              const expObj = parts.length === 3
                ? new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
                : new Date(expDate);
              const daysUntil = Math.ceil((expObj - now) / (1000*60*60*24));
              if (daysUntil > 60) return; // too far out
            }
            allTodos.push({
              id: `ins-${i}-${Date.now()}`,
              title: 'Insurance Expiring',
              subtitle: p.Property_Name ?? 'N/A',
              type: 'insurance',
              dueDate: expDate,
              completed: false
            });
          });
        }
      } catch (err) { console.error('Error fetching insurance:', err); }

      try {
        // 4️⃣ Leases Ending Soon — show within 90 days (and after)
        const leaseRes = await fetch(`${BASE_URL}/LeasesEndingSoon?pm_email=${encodeURIComponent(email)}`);
        if (leaseRes.ok) {
          const data = await leaseRes.json();
          (data.leases_ending_soon || []).forEach((l, i) => {
            const endDate = l.Lease_End_Date ?? null;
            if (endDate) {
              const now = new Date(); now.setHours(0,0,0,0);
              const parts = String(endDate).split('T')[0].split('-');
              const endObj = parts.length === 3
                ? new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
                : new Date(endDate);
              const daysUntil = Math.ceil((endObj - now) / (1000*60*60*24));
              if (daysUntil > 90) return; // too far out
            }
            allTodos.push({
              id: `lease-${i}-${Date.now()}`,
              title: 'Lease Ending',
              subtitle: `${l.Property_Name}${l.Unit_Number ? ` · Unit ${l.Unit_Number}` : ''}`,
              type: 'lease',
              dueDate: endDate,
              completed: false
            });
          });
        }
      } catch (err) { console.error('Error fetching leases:', err); }

      try {
        // 5️⃣ Upcoming / Past Due Rent
        // Rent Collection: shows 7 days before due date
        // Past Due Rent:   shows 3+ days after due date (1-2 days = grace period, skip)
        const rentRes = await fetch(`${BASE_URL}/UpcomingRent?pm_email=${encodeURIComponent(email)}`);
        if (rentRes.ok) {
          const data = await rentRes.json();
          (data.upcoming_rent || []).forEach((r, i) => {
            const dueDate = r.rent_due_date || r.Due_Date;
            if (!dueDate) return;
            const now = new Date(); now.setHours(0,0,0,0);
            const parts = String(dueDate).split('T')[0].split('-');
            const dueDateObj = parts.length === 3
              ? new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
              : new Date(dueDate);
            const daysUntilDue = Math.ceil((dueDateObj - now) / (1000*60*60*24));
            if (daysUntilDue > 7) return;             // too far out — don't show yet
            if (daysUntilDue < 0 && daysUntilDue > -3) return; // 1-2 day grace period
            const overdue = daysUntilDue <= -3;
            allTodos.push({
              id: `rent-${i}-${Date.now()}`,
              title: overdue ? 'Past Due Rent' : 'Rent Collection',
              subtitle: `${r.Property_Name}${r.Unit_Number ? ` · Unit ${r.Unit_Number}` : ''}`,
              amount: r.amount ? `$${Number(r.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : undefined,
              type: overdue ? 'pastdue' : 'rent',
              dueDate,
              completed: false
            });
          });
        }
      } catch (err) { console.error('Error fetching upcoming rent:', err); }

      setTodos(allTodos);

      // Work Orders
      try {
        const woRes = await fetch(`${BASE_URL}/GetWorkOrdersByPM?pm_email=${encodeURIComponent(email)}`);
        if (woRes.ok) {
          const d = await woRes.json();
          const list = d.work_orders || d.workOrders || d.work_order_list || (Array.isArray(d) ? d : []);
          setWorkOrders(list);
        }
      } catch (err) { console.error('Error fetching work orders:', err); }

      // Work Orders Summary (MTD card)
      try {
        const wsRes = await fetch(`${BASE_URL}/WorkOrderSummary?pm_email=${encodeURIComponent(email)}`);
        if (wsRes.ok) {
          const wsData = await wsRes.json();
          console.log('[WorkOrderSummary]', wsData);
          setWoSummary(wsData);
        }
      } catch (err) { console.error('Error fetching work order summary:', err); }

      // Upcoming / overdue work orders
      try {
        const uwoRes = await fetch(`${BASE_URL}/UpcomingWorkOrders?pm_email=${encodeURIComponent(email)}`);
        if (uwoRes.ok) {
          const uwoData = await uwoRes.json();
          const uwoList = uwoData.work_orders || uwoData.upcoming_work_orders || (Array.isArray(uwoData) ? uwoData : []);
          const now = new Date(); now.setHours(0,0,0,0);
          uwoList.forEach((wo, i) => {
            const rawDate = wo.Due_Date || wo.due_date || null;
            let daysUntil = null;
            if (rawDate) {
              const parts = String(rawDate).split('T')[0].split('-');
              const d = parts.length === 3
                ? new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
                : new Date(rawDate);
              daysUntil = Math.ceil((d - now) / (1000*60*60*24));
            }
            const overdue = daysUntil !== null && daysUntil < 0;
            allTodos.push({
              id: `wo-${wo.id || i}-${Date.now()}`,
              title: overdue ? 'Overdue Work Order' : 'Work Order Due Soon',
              subtitle: `${wo.Title || 'Untitled'}${wo.Property_Name ? ` · ${wo.Property_Name}` : ''}${wo.Unit_Number ? ` Unit ${wo.Unit_Number}` : ''}`,
              type: overdue ? 'pastdue' : 'workorder',
              dueDate: rawDate,
              completed: false,
            });
          });
        }
      } catch (err) { console.error('Error fetching upcoming work orders:', err); }

      // Cash flow chart
      //try {
        //const chartRes = await fetch(`${BASE_URL}/HomeChartCashFlow?pm_email=${encodeURIComponent(email)}&range=ytd`);
       // if (chartRes.ok) {
        //  const d = await chartRes.json();
       //   if (Array.isArray(d.labels) && Array.isArray(d.values)) {
       //     setCashFlowData(d.labels.map((label, i) => {
       //       const value = Number(d.values[i]) || 0;
      //        return { month: label, cashFlow: value, isPositive: value >= 0 };
       //     }));
       //   }
      //  }
     // } catch (err) { console.error('Error fetching cash flow:', err); }

      // Recent activity + income/expense chart data
      try {
        const [expRes, rentPayRes] = await Promise.all([
          fetch(`${BASE_URL}/ListExpense?pm_email=${encodeURIComponent(email)}`),
          fetch(`${BASE_URL}/ListRentPayments?pm_email=${encodeURIComponent(email)}&start_month=1&start_year=${year}&end_month=${month}&end_year=${year}`)
        ]);

        const activities = [];

        if (expRes.ok) {
          const d = await expRes.json();
          const raw = d.expenses || d.Expenses || (Array.isArray(d) ? d : []);
          raw.forEach((e) => {
            activities.push({
              date: e.paydate || e.PayDate || e.Paydate || '',
              amount: -Math.abs(parseFloat(e.amount || e.Amount || 0)),
              label: e.title || e.Title || e.category || 'Expense',
              property: e.property_name || e.Property_Name || '',
              type: 'expense'
            });
          });
          const catMap = {};
          raw.forEach((e) => {
            const date =
              e.paydate ||
              e.PayDate ||
              e.Paydate ||
              e.date ||
              e.Date;

            if (!date) return;

            const d = new Date(date);

            // ONLY current month + current year
            if (
              d.getMonth() + 1 !== month ||
              d.getFullYear() !== year
            ) {
              return;
            }

            const cat =
              e.category ||
              e.Category ||
              e.title ||
              e.Title ||
              'Other';

            catMap[cat] =
              (catMap[cat] || 0) +
              Math.abs(parseFloat(e.amount || e.Amount || 0));
          });
          setExpenseCategoryList(Object.entries(catMap).map(([cat, total]) => ({ cat, total })).sort((a, b) => b.total - a.total));
        }

        if (rentPayRes.ok) {
          const d = await rentPayRes.json();
          let rentsRaw = [];
          if (Array.isArray(d.items)) {
            rentsRaw = d.items;
          } else if (Array.isArray(d.rent)) {
            rentsRaw = d.rent;
          } else if (Array.isArray(d.rent_payments)) {
            rentsRaw = d.rent_payments;
          } else if (Array.isArray(d.RentPayments)) {
            rentsRaw = d.RentPayments;
          }

          rentsRaw.forEach((r) => {
            // Skip rent forgiveness — it's a write-off, not received cash
            if (r.item_type === 'rent_forgiveness' || r.Category === 'RentForgiveness') return;
            const amt = r.amount ?? r.Amount ?? r.rent ?? r.Rent ?? r.payment_amount ?? r.Payment_Amount ?? 0;
            activities.push({
              date: r.Paydate || r.timestamp || r.paydate || r.PayDate || r.date || r.Date || '',
              amount: Math.abs(parseFloat(amt) || 0),
              label: r.Full_Name || r.tenant_name || r.Tenant_Name || 'Rent Payment',
              property: r.property_name || r.Property_Name || '',
              type: 'income'
            });
          });
        }

        activities.sort((a, b) => new Date(b.date) - new Date(a.date));
        setAllActivities([...activities]);
        setRecentActivity(activities.slice(0, 5));

        const income = activities
          .filter(a => a.type === 'income')
          .reduce((s, a) => s + Math.abs(a.amount || 0), 0);

        const expenses = activities
          .filter(a => a.type === 'expense')
          .reduce((s, a) => s + Math.abs(a.amount || 0), 0);

        const ytdIncomeTotal = activities
          .filter(a => a.type === 'income' && !isNaN(a.amount))
          .reduce((sum, a) => sum + Math.abs(a.amount), 0);

        const ytdExpenseTotal = activities
          .filter(a => a.type === 'expense')
          .reduce((sum, a) => sum + Math.abs(a.amount || 0), 0);

        // ONLY SET ONCE
        setYtdIncome(ytdIncomeTotal);
        setYtdExpenses(ytdExpenseTotal);

        

        // Aggregate by month for income vs expenses chart — current year only
        const monthLabels = [
          'Jan','Feb','Mar','Apr','May','Jun',
          'Jul','Aug','Sep','Oct','Nov','Dec'
        ];

        const monthlyMap = Array.from({ length: 12 }, (_, i) => ({
          monthIndex: i,
          income: 0,
          expenses: 0
        }));

        activities.forEach((item) => {
          if (!item.date) return;

          const d = new Date(item.date);
          if (isNaN(d.getTime())) return;
          if (d.getFullYear() !== year) return;

          const m = d.getMonth();

          if (item.type === 'income') {
            monthlyMap[m].income += Math.abs(item.amount || 0);
          } else {
            monthlyMap[m].expenses += Math.abs(item.amount || 0);
          }
        });

        const chartData = monthlyMap.map((m, i) => ({
          key: `${year}-${String(i + 1).padStart(2, '0')}`,
          income: m.income,
          expenses: m.expenses,
          monthLabel: monthLabels[i]
        }));

        const filtered = chartData.filter(
          (m) => m.income !== 0 || m.expenses !== 0
        );

        setMonthlyIncomeExpense(filtered);

      } catch (err) {
        console.error('Error fetching activity data:', err);
      }

      // Inject sample data for brand-new managers with no real data yet
      if (localStorage.getItem(`isDemoMode_${email}`) === 'true') {
        const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const now2 = new Date();
        const demoMonths = Array.from({length: 6}, (_, i) => {
          const d = new Date(now2.getFullYear(), now2.getMonth() - 5 + i, 1);
          return MONTHS[d.getMonth()];
        });
        const demoCF  = [1620, 1680, 1510, 1550, 1480, 1510];
        const demoExp = [780,  720,  890,  850,  920,  890];
        setOccupancyData(p      => p.total === 0   ? { occupied: 1, total: 1 } : p);
        setPropertyStats(p      => p.total === 0   ? { total: 1, units: 1, vacant: 0, occupied: 1 } : p);
        setTotalIncome(p        => p.current === 0 ? { current: 2400, lastMonth: 2400 } : p);
        setTotalExpenses(p      => p.current === 0 ? { current: 890,  lastMonth: 720  } : p);
        setCashFlowData(p       => p.length === 0  ? demoMonths.map((m, i) => ({ month: m, cashFlow: demoCF[i],  isPositive: true })) : p);
        setMonthlyIncomeExpense(p => p.length === 0 ? demoMonths.map((m, i) => ({ month: m, income: 2400, expenses: demoExp[i] })) : p);
        setYtdIncome(p          => p === 0 ? 2400 * (now2.getMonth() + 1) : p);
        setYtdExpenses(p        => p === 0 ? 890  * (now2.getMonth() + 1) : p);
        setYtdCashFlow(p        => p === 0 ? 1510 * (now2.getMonth() + 1) : p);
        setRecentActivity(p     => p.length === 0 ? [{
          id: 'demo-rent-1', type: 'income', amount: 2400,
          date: new Date().toISOString(),
          description: 'Rent Payment — Sample Property', category: 'Rent',
        }] : p);
        setDemoMode(true);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };

  const deleteTodo = async (todo) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      if (todo.type === 'manual' || todo.type === 'reminder') {
        await fetch(`${BASE_URL}/DeleteReminder?Email=${encodeURIComponent(user.email)}&Text=${encodeURIComponent(todo.text)}`, { method: 'DELETE' });
      }
      setTodos(prev => prev.filter(t => t.id !== todo.id));
    } catch (err) { console.error('Error deleting reminder:', err); }
  };

  const addReminder = async () => {
    if (!reminderText.trim()) return;
    const user = auth.currentUser;
    if (!user) return;
    setReminderSubmitting(true);
    try {
      let url = `${BASE_URL}/AddReminder?Text=${encodeURIComponent(reminderText.trim())}&Email=${encodeURIComponent(user.email)}`;
      if (reminderDueDate) url += `&Due_Date=${encodeURIComponent(reminderDueDate)}`;
      const res = await fetch(url, { method: 'POST' });
      if (res.ok) {
        setTodos(prev => [...prev, {
          id: `rem-new-${Date.now()}`,
          title: 'Reminder',
          subtitle: reminderText.trim(),
          type: 'manual',
          dueDate: reminderDueDate || null,
          completed: false
        }]);
        setReminderText('');
        setReminderDueDate('');
        setShowAddReminder(false);
      }
    } catch (err) {
      console.error('Error adding reminder:', err);
    } finally {
      setReminderSubmitting(false);
    }
  };

  /* ── Onboarding wizard handlers ── */
  const finishOnboarding = () => {
    localStorage.removeItem('isNewManager');
    if (userEmail) localStorage.removeItem(`hasSeenHomeTutorial_${userEmail}`);
    setOnboardStep(0);
  };

  const handleOnboardBank = async () => {
    if (!userEmail) return;
    await auth.currentUser?.reload();
    if (!auth.currentUser?.emailVerified) { setShowVerifyModal(true); return; }
    setObBankConnecting(true);
    setObBankMsg('');
    try {
      if (!plaidReady.current) {
        await new Promise((res, rej) => {
          const s = document.getElementById('plaid-link-sdk');
          if (s) { plaidReady.current = true; res(); return; }
          const el = document.createElement('script');
          el.id = 'plaid-link-sdk';
          el.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
          el.onload = () => { plaidReady.current = true; res(); };
          el.onerror = rej;
          document.head.appendChild(el);
        });
      }
      const tokenRes = await fetch(`${BASE_URL}/CreatePlaidLinkToken?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST', headers: { accept: 'application/json' },
      });
      if (!tokenRes.ok) throw new Error('Could not create link token');
      const { link_token } = await tokenRes.json();
      if (!link_token) throw new Error('No link token returned');

      const handler = window.Plaid.create({
        token: link_token,
        onSuccess: async (publicToken) => {
          try {
            const exRes = await fetch(`${BASE_URL}/ExchangePlaidPublicToken?user_email=${encodeURIComponent(userEmail)}&public_token=${encodeURIComponent(publicToken)}`, {
              method: 'POST', headers: { accept: 'application/json' },
            });
            if (!exRes.ok) throw new Error('Failed to exchange token');
            const { plaid_connection_id: connId } = await exRes.json();
            await fetch(`${BASE_URL}/SyncPlaidTransactions?user_email=${encodeURIComponent(userEmail)}&plaid_connection_id=${encodeURIComponent(connId)}`, {
              method: 'POST', headers: { accept: 'application/json' },
            });
            setObBankStatus('success');
          } catch (err) {
            setObBankStatus('error');
            setObBankMsg(err.message || 'Something went wrong');
          } finally {
            setObBankConnecting(false);
          }
        },
        onExit: (err) => {
          setObBankConnecting(false);
          if (err) { setObBankStatus('error'); setObBankMsg(err.display_message || 'Cancelled'); }
        },
      });
      handler.open();
    } catch (err) {
      setObBankConnecting(false);
      setObBankStatus('error');
      setObBankMsg(err.message || 'Failed to start bank connection');
    }
  };

  const handleOnboardStripe = async () => {
    if (!pmId) return;
    setObStripeLoading(true);
    try {
      await fetch(`${BASE_URL}/Stripe/CreateConnectedAccount?pm_id=${pmId}&email=${encodeURIComponent(userEmail)}`, { method: 'POST' });
      const linkRes = await fetch(`${BASE_URL}/Stripe/CreateOnboardingLink?pm_id=${pmId}`, { method: 'POST' });
      if (!linkRes.ok) throw new Error('Failed to generate onboarding link');
      const linkData = await linkRes.json();
      const url = linkData?.url ?? linkData;
      if (typeof url === 'string' && url.startsWith('http')) {
        window.open(url, '_blank');
        setObStripeOpened(true);
      } else {
        throw new Error('Invalid URL received');
      }
    } catch (err) {
      console.error('Stripe onboarding error:', err);
    } finally {
      setObStripeLoading(false);
    }
  };

  const openAnnouncementModal = async () => {
    setShowAnnouncement(true);
    setAnnTitle('');
    setAnnMessage('');
    setAnnExpDays(30);
    setAnnSelected(new Set());
    setAnnSuccess(false);
    setAnnError('');
    setAnnDoorsLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(userData?.Full_Name || '')}&Email=${encodeURIComponent(userEmail)}`
      );
      if (res.ok) {
        const data = await res.json();
        const doors = (data.Properties || []).filter(p => p.Door_id);
        setAnnDoors(doors.map(p => ({
          door_id: p.Door_id,
          property_name: p.Property_Name || 'Unknown Property',
          unit_number: p.Unit_Number || ''
        })));
      }
    } catch (err) {
      console.error('Error fetching properties for announcement:', err);
    } finally {
      setAnnDoorsLoading(false);
    }
  };

  const toggleAnnDoor = (door_id) => {
    setAnnSelected(prev => {
      const next = new Set(prev);
      if (next.has(door_id)) next.delete(door_id);
      else next.add(door_id);
      return next;
    });
  };

  const toggleAnnProperty = (doors) => {
    const ids = doors.map(d => d.door_id);
    const allSel = ids.every(id => annSelected.has(id));
    setAnnSelected(prev => {
      const next = new Set(prev);
      if (allSel) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const submitAnnouncement = async () => {
    if (!annTitle.trim() || !annMessage.trim() || annSelected.size === 0) return;
    setAnnSubmitting(true);
    setAnnError('');
    const pm_id = pmId || userData?.id || userData?.PM_ID || userData?.pm_id || userData?.Manager_ID || 0;
    try {
      await Promise.all([...annSelected].map(async (door_id) => {
        const res = await fetch(
          `${BASE_URL}/AddNewAnnouncement?door_id=${door_id}&title=${encodeURIComponent(annTitle.trim())}&message=${encodeURIComponent(annMessage.trim())}&pm_id=${pm_id}&expiration_days=${annExpDays}`,
          { method: 'POST' }
        );
        if (!res.ok) {
          const errText = await res.text().catch(() => res.statusText);
          throw new Error(errText || `Server error ${res.status}`);
        }
      }));
      setAnnSuccess(true);
      setTimeout(() => {
        setShowAnnouncement(false);
        setAnnSuccess(false);
      }, 1800);
    } catch (err) {
      console.error('Error sending announcement:', err);
      setAnnError(err.message || 'Failed to send. Please try again.');
    } finally {
      setAnnSubmitting(false);
    }
  };

  const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    return Number(String(value).replace(/[^0-9.-]+/g, '')) || 0;
  };

  const fmt = (amount) =>
    Math.abs(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtShort = (amount) =>
    Math.abs(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // 0=past-due rent, 1=other overdue, 2=0-3 days, 3=4+ days, 4=no date
  const parseLocalDate = (d) => {
    if (!d) return null;
    const s = String(d).split('T')[0];
    const parts = s.split('-');
    if (parts.length === 3 && parts[0].length === 4)
      return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return new Date(d);
  };

  const getTodoUrgency = (todo) => {
    if (todo.type === 'pastdue') return 0; // past due rent — highest priority
    if (todo.dueDate) {
      const days = Math.ceil((parseLocalDate(todo.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (days < 0)  return 1; // other overdue (lease ended, insurance expired)
      if (days <= 3) return 2; // due within 0-3 days
      return 3;                // due 4+ days out
    }
    return 4; // no due date
  };

  // levels 0 and 1 both use overdue red — only sort order differs
  const urgencyClass = ['tc-urgency-overdue', 'tc-urgency-overdue', 'tc-urgency-urgent', 'tc-urgency-upcoming', 'tc-urgency-ndate'];

  // Derived values
  const netCashFlow = (totalIncome.current || 0) - (totalExpenses.current || 0);
  const occupancyRate = occupancyData.total > 0
    ? Math.round((occupancyData.occupied / occupancyData.total) * 100)
    : 0;
  const expenseRatio =
    totalIncome.current > 0
      ? Math.round((totalExpenses.current / totalIncome.current) * 100)
      : 0;

  // Convert "2026-01" → "Jan", or strip year from "January, 2026" → "Jan"
  const toShortMonth = (label) => {
    if (/^\d{4}-\d{2}$/.test(String(label))) {
      return new Date(label + '-01').toLocaleString('default', { month: 'short' });
    }
    const name = String(label).replace(/,\s*\d{4}$/, '').trim();
    return name.slice(0, 3);
  };

  // Clean y-axis number format
  const fmtAxis = (val) => {
    const abs = Math.abs(val);
    const sign = val < 0 ? '-' : '';
    if (abs >= 10000) return `${sign}${Math.round(abs / 1000)}k`;
    if (abs >= 1000)  return `${sign}${(abs / 1000).toFixed(1)}k`;
    return Math.round(val).toString();
  };

  // ---- Mini Chart inside Portfolio Pulse (green) ----
  const renderMiniChart = (data) => {
    if (!data || data.length < 2) return null;
    const W = 420, H = 110, pL = 4, pR = 4, pT = 8, pB = 22;
    const iW = W - pL - pR, iH = H - pT - pB;
    const vals = data.map(d => d.cashFlow);
    const minV = Math.min(0, ...vals);
    const maxV = Math.max(...vals, 1);
    const range = maxV - minV || 1;
    const xS = (i) => pL + (i / (data.length - 1)) * iW;
    const yS = (v) => pT + iH - ((v - minV) / range) * iH;
    const pts = data.map((d, i) => ({ x: xS(i), y: yS(d.cashFlow) }));
    let line = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
      const cp = ((pts[i-1].x + pts[i].x) / 2).toFixed(1);
      line += ` C ${cp} ${pts[i-1].y.toFixed(1)} ${cp} ${pts[i].y.toFixed(1)} ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`;
    }
    const baseY = yS(0);
    const area = `${line} L ${pts[pts.length-1].x.toFixed(1)} ${baseY} L ${pts[0].x.toFixed(1)} ${baseY} Z`;
    const segW = data.length > 1 ? iW / (data.length - 1) : iW;

    const renderMiniTT = () => {
      if (miniTooltip === null || !data[miniTooltip]) return null;
      const d = data[miniTooltip];
      const x = xS(miniTooltip);
      const y = yS(d.cashFlow);
      const val = d.cashFlow;
      const tW = 82, tH = 38;
      const tX = Math.min(Math.max(x - tW / 2, pL + 2), W - tW - pR - 2);
      const tY = Math.max(y - tH - 10, pT + 2);
      const valStr = (val >= 0 ? '+' : '-') + '$' + Math.abs(val).toLocaleString('en-US', { maximumFractionDigits: 0 });
      return (
        <g>
          <line x1={x} y1={tY + tH + 2} x2={x} y2={y - 4} stroke="#22c55e" strokeWidth="1" strokeDasharray="2,2" opacity="0.5" />
          <rect x={tX} y={tY} width={tW} height={tH} rx="6" fill="#1e293b" />
          <text x={tX + tW / 2} y={tY + 13} textAnchor="middle" fontSize="9" fill="#94a3b8">{toShortMonth(d.month)}</text>
          <text x={tX + tW / 2} y={tY + 28} textAnchor="middle" fontSize="10.5" fontWeight="700" fill={val >= 0 ? '#34d399' : '#f87171'}>{valStr}</text>
          <circle cx={x} cy={y} r="3.5" fill="#22c55e" stroke="#fff" strokeWidth="1.5" />
        </g>
      );
    };

    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
           className="pp-mini-chart-svg"
           style={{ width: '100%', height: 'auto', display: 'block' }}
           onMouseLeave={() => setMiniTooltip(null)}>
        <path d={line} fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => {
          const skip = data.length > 6 && i % Math.ceil(data.length / 6) !== 0;
          return skip ? null : (
            <text key={i} x={xS(i)} y={H - 4} textAnchor="middle" fontSize="9" fill="#b0b8c8">
              {toShortMonth(d.month)}
            </text>
          );
        })}
        {/* Hover hit bands */}
        {data.map((d, i) => {
          const cx = xS(i);
          const hx = Math.max(pL, cx - segW / 2);
          const hw = Math.min(pL + iW, cx + segW / 2) - hx;
          return (
            <rect key={`mhit-${i}`} x={hx} y={pT} width={hw} height={iH}
                  fill="transparent" style={{ cursor: 'crosshair' }}
                  onMouseEnter={() => setMiniTooltip(i)} />
          );
        })}
        {renderMiniTT()}
      </svg>
    );
  };

  // ---- SVG Area Chart (Trailing 12 Months) ----
  const renderAreaChart = (data) => {
    if (!data || data.length < 2) {
      return <div className="chart-no-data">No data available</div>;
    }

    const W = 580, H = 150, pL = 42, pR = 8, pT = 10, pB = 28;
    const iW = W - pL - pR, iH = H - pT - pB;

    const vals = data.map(d => d.cashFlow);
    const minV = Math.min(0, ...vals);
    const maxV = Math.max(...vals, 1);
    const range = maxV - minV || 1;

    const xS = (i) => pL + (i / (data.length - 1)) * iW;
    const yS = (v) => pT + iH - ((v - minV) / range) * iH;

    const pts = data.map((d, i) => ({ x: xS(i), y: yS(d.cashFlow) }));

    let line = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
      const cp = ((pts[i - 1].x + pts[i].x) / 2).toFixed(1);
      line += ` C ${cp} ${pts[i - 1].y.toFixed(1)} ${cp} ${pts[i].y.toFixed(1)} ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`;
    }
    const baseY = yS(0);
    const area = `${line} L ${pts[pts.length - 1].x.toFixed(1)} ${baseY} L ${pts[0].x.toFixed(1)} ${baseY} Z`;

    const yLevels = [0, 0.25, 0.5, 0.75, 1];

    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '150px', display: 'block' }}>
        <defs>
          <linearGradient id="cashAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {yLevels.map((lvl, i) => {
          const val = minV + range * lvl;
          const y = yS(val);
          return (
            <g key={i}>
              <line x1={pL} y1={y} x2={pL + iW} y2={y} stroke="#f0f0f0" strokeWidth="1" />
              <text x={pL - 4} y={y + 3} textAnchor="end" fontSize="9" fill="#b0b8c8">
                {fmtAxis(val)}
              </text>
            </g>
          );
        })}
        {/* Zero baseline (darker) when chart has negative values */}
        {minV < 0 && (
          <line x1={pL} y1={yS(0)} x2={pL + iW} y2={yS(0)} stroke="#d1d5db" strokeWidth="1" strokeDasharray="3,2" />
        )}
        <path d={area} fill="url(#cashAreaGrad)" />
        <path d={line} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => (
          <circle key={i} cx={xS(i)} cy={yS(d.cashFlow)} r="2.5" fill="#3b82f6" />
        ))}
        {data.map((d, i) => {
          const skip = data.length > 8 && i % Math.ceil(data.length / 8) !== 0;
          return skip ? null : (
            <text key={i} x={xS(i)} y={H - 5} textAnchor="middle" fontSize="9" fill="#b0b8c8">
              {toShortMonth(d.month)}
            </text>
          );
        })}
      </svg>
    );
  };

  // ---- SVG Bar Chart (Income vs Expenses) ----
  const renderBarChart = (data) => {
    if (!data || data.length === 0) {
      return <div className="chart-no-data">No data available</div>;
    }

    const W = 580, H = 260, pL = 34, pR = 8, pT = 18, pB = 32;
    const iW = W - pL - pR, iH = H - pT - pB;

    const maxVal = Math.max(...data.map(d => Math.max(d.income, d.expenses)), 1);
    const yLevels = [0, 0.25, 0.5, 0.75, 1];
    const groupW = iW / data.length;
    const barW = Math.min(groupW * 0.24, 18);
    const gap = 5;
    const baseY = pT + iH;

    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMinYMid meet"
           style={{ width: '100%', height: '100%', display: 'block' }}
           onMouseLeave={() => setBarTooltip(null)}>
        <defs>
          <linearGradient id="incomeBarGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
          <linearGradient id="expenseBarGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#b91c1c" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {yLevels.map((lvl, i) => {
          const val = maxVal * lvl;
          const y = pT + iH * (1 - lvl);
          return (
            <g key={i}>
              <line
                x1={pL} y1={y} x2={pL + iW} y2={y}
                stroke={i === 0 ? '#e2e8f0' : '#e9eef5'}
                strokeWidth="1"
                strokeDasharray={i === 0 ? '' : '4,3'}
              />
              <text x={pL - 7} y={y + 3.5} textAnchor="end" fontSize="9.5" fill="#94a3b8">
                {fmtAxis(val)}
              </text>
            </g>
          );
        })}

        {/* Bars with hover tooltips */}
        {data.map((d, i) => {
          const cx = pL + (i + 0.5) * groupW;
          const incH = Math.max((d.income / maxVal) * iH, 3);
          const expH = Math.max((d.expenses / maxVal) * iH, 3);
          const isHovered = barTooltip === i;
          const tW = 112, tH = 58;
          const tX = Math.min(Math.max(cx - tW / 2, pL + 2), W - tW - pR - 2);
          const tY = pT + 6;
          return (
            <g key={i} onMouseEnter={() => setBarTooltip(i)} style={{ cursor: 'pointer' }}>
              {/* Hover background highlight */}
              {isHovered && (
                <rect x={cx - groupW / 2 + 2} y={pT} width={groupW - 4} height={iH} rx="6" fill="#f1f5f9" />
              )}
              <rect x={cx - barW - gap / 2} y={baseY - incH} width={barW} height={incH} rx="5" ry="5"
                    fill="url(#incomeBarGrad)" opacity={barTooltip !== null && !isHovered ? 0.4 : 1} />
              <rect x={cx + gap / 2} y={baseY - expH} width={barW} height={expH} rx="5" ry="5"
                    fill="url(#expenseBarGrad)" opacity={barTooltip !== null && !isHovered ? 0.4 : 1} />
              <text x={cx} y={H - 10} textAnchor="middle" fontSize="9.5" fontWeight="500"
                    fill={isHovered ? '#475569' : '#94a3b8'}>{d.monthLabel}</text>
              {/* Tooltip box */}
              {isHovered && (
                <g>
                  <rect x={tX} y={tY} width={tW} height={tH} rx="7" fill="#1e293b" />
                  <text x={tX + tW / 2} y={tY + 15} textAnchor="middle" fontSize="10" fontWeight="700" fill="#f1f5f9">{d.monthLabel}</text>
                  <circle cx={tX + 14} cy={tY + 29} r="4" fill="#34d399" />
                  <text x={tX + 24} y={tY + 33} fontSize="9.5" fill="#94a3b8">Income</text>
                  <text x={tX + tW - 8} y={tY + 33} textAnchor="end" fontSize="9.5" fontWeight="600" fill="#34d399">${fmtShort(d.income)}</text>
                  <circle cx={tX + 14} cy={tY + 45} r="4" fill="#ef4444" />
                  <text x={tX + 24} y={tY + 49} fontSize="9.5" fill="#94a3b8">Expenses</text>
                  <text x={tX + tW - 8} y={tY + 49} textAnchor="end" fontSize="9.5" fontWeight="600" fill="#ef4444">${fmtShort(d.expenses)}</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  if (loading) return <LoadingScreen />;

  const completedCount = todos.filter(t => t.completed).length;
  const progressPct = todos.length > 0 ? (completedCount / todos.length) * 100 : 0;

  // Format "2026-01" → "Jan, 2026", pass through anything else
  const formatCfLabel = (l) => {
    if (/^\d{4}-\d{2}$/.test(String(l))) {
      const d = new Date(l + '-01');
      return d.toLocaleString('default', { month: 'short' }) + ', ' + d.getFullYear();
    }
    return l;
  };

  // Strip year suffix — "January, 2026" → "January"
  const stripYear = (label) => String(label || '—').replace(/,\s*\d{4}$/, '').trim();

  // Modal computed data — all derived from cashFlowByMonth { label, value }
  const modalNetCashFlow = cashFlowByMonth.reduce((sum, m) => sum + Number(m.value || 0), 0);

  const safeCashFlow = Array.isArray(cashFlowByMonth) ? cashFlowByMonth : [];

const modalBestMonth =
  safeCashFlow.length > 0
    ? safeCashFlow.reduce((a, b) => (Number(b?.value || 0) > Number(a?.value || 0) ? b : a))
    : null;

const modalWorstMonth =
  safeCashFlow.length > 0
    ? safeCashFlow.reduce((a, b) => (Number(b?.value || 0) < Number(a?.value || 0) ? b : a))
    : null;

  const HOME_STEPS = [
    { title: 'Welcome to Rside!', description: "Let's take a quick tour of your Home dashboard so you can hit the ground running.", anchorId: null, cardAlignment: 'bottom' },
    { title: 'Portfolio Pulse', description: "Your financial snapshot at a glance — see this month's income, cash flow, and occupancy rate all in one card.", anchorId: 'home-portfolio' },
    { title: 'Quick Access Metrics', description: 'Tap any tile to open a detailed breakdown — YTD income, cash flow, expense ratio, and occupancy each have their own detail view.', anchorId: 'home-stats' },
    { title: 'Income vs Expenses', description: "A month-by-month bar chart so you can instantly spot trends, compare income to spending, and catch any unusual months.", anchorId: 'home-chart' },
    { title: 'Work Orders', description: 'See your open and in-progress maintenance requests at a glance. Tap the card to view the full list.', anchorId: 'home-work-quick' },
    { title: 'Quick Actions', description: 'One tap to add an expense, log income, or run a financial projection — no need to navigate away from home.', anchorId: 'home-quick-actions' },
    { title: 'Send Announcements', description: 'Keep your tenants in the loop — tap "Send Announcement" to broadcast a message to any or all of your units. Set an expiration date and pick specific recipients.', anchorId: 'home-announcement' },
    { title: 'Smart Reminders', description: 'Stay on top of upcoming rent, expiring leases, insurance renewals, and any custom reminders you set.', anchorId: 'home-reminders' },
    { title: 'Recent Activity', description: 'Your latest income and expense transactions, always visible so nothing slips through the cracks.', anchorId: 'home-recent-activity' },
  ];

  return (
    <div className="home-page">
      {showVerifyModal && (
        <EmailVerifyModal
          onVerified={() => { setShowVerifyModal(false); handleOnboardBank(); }}
          onCancel={() => setShowVerifyModal(false)}
        />
      )}
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar
          title="Dashboard"
          subtitle={`All Properties, ${now.toLocaleString('default', { month: 'short' })}. ${now.getDate()}`}
        />
        <div className="dashboard-container">
          {demoMode && (
            <div className="demo-mode-banner">
              <span className="demo-mode-icon">📊</span>
              Sample data — add your first property to see real numbers
            </div>
          )}
          <div className="dashboard-layout">

            {/* ======= LEFT PANEL ======= */}
            <div className="dashboard-left">

              {/* Portfolio Pulse Card */}
              <div id="home-portfolio" className="portfolio-pulse-card">
                {/* Top row: left = title+amount, right = mini chart */}
                <div className="pp-main-row">
                  <div className="pp-left">
                    <div className="pp-top">
                      <span className="pp-label">PORTFOLIO PULSE</span>
                      <span className="pp-period">Cash Flow, {monthName}</span>
                    </div>
                    <div className="pp-big-value" style={{ color: netCashFlow < 0 ? '#dc2626' : '#16a34a' }}>
                    {netCashFlow < 0 ? '-' : ''}${fmt(netCashFlow)}
                  </div>
                  </div>
                  <div className="pp-right">
                    {renderMiniChart(cashFlowData)}
                  </div>
                </div>
                {/* Divider */}
                <div className="pp-divider" />
                {/* Metrics row */}
                <div className="pp-metrics-row">
                  <div className="pp-metric">
                    <span className="pp-metric-label">Income</span>
                    <span className="pp-metric-value pp-green">${fmt(totalIncome.current)}</span>
                  </div>
                  <div className="pp-metric">
                    <span className="pp-metric-label">Expenses</span>
                    <span className="pp-metric-value pp-red">${fmt(totalExpenses.current)}</span>
                  </div>
                  <div className="pp-metric">
                    <span className="pp-metric-label">Occupancy</span>
                    <span className="pp-metric-value pp-blue">{occupancyRate}%</span>
                  </div>
                </div>
              </div>

              {/* 4 Stat Cards */}
              <div id="home-stats" className="pulse-stats-row">
                <div className="pulse-stat-card pulse-stat-clickable" onClick={() => setActiveModal('income')}>
                  <div className="stat-icon-wrap stat-icon-green"><FaDollarSign /></div>
                  <div className="stat-info">
                    <div className="stat-value" style={{ color: ytdIncome < 0 ? '#dc2626' : '#16a34a' }}>
                      {ytdIncome < 0 ? '-' : ''}${fmtShort(ytdIncome)}
                    </div>
                    <div className="stat-label">YTD Income</div>
                  </div>
                </div>
                <div className="pulse-stat-card pulse-stat-clickable" onClick={() => setActiveModal('cashflow')}>
                  <div className="stat-icon-wrap stat-icon-blue"><FaArrowUp /></div>
                  <div className="stat-info">
                    <div className="stat-value" style={{ color: ytdCashFlow < 0 ? '#dc2626' : '#16a34a' }}>
                      {ytdCashFlow < 0 ? '-' : ''}${fmtShort(ytdCashFlow)}
                    </div>
                    <div className="stat-label">YTD Cash Flow</div>
                  </div>
                </div>
                <div className="pulse-stat-card pulse-stat-clickable" onClick={() => setActiveModal('expense')}>
                  <div className="stat-icon-wrap stat-icon-orange"><FaPercent /></div>
                  <div className="stat-info">
                    <div className="stat-value">{expenseRatio}%</div>
                    <div className="stat-label">Expense Ratio</div>
                  </div>
                </div>
                <div className="pulse-stat-card pulse-stat-clickable" onClick={() => setActiveModal('occupancy')}>
                  <div className="stat-icon-wrap stat-icon-teal"><FaBuilding /></div>
                  <div className="stat-info">
                    <div className="stat-value">{propertyStats.vacant} vacant</div>
                    <div className="stat-label">{propertyStats.units} unit{propertyStats.units !== 1 ? 's' : ''}</div>
                  </div>
                </div>
              </div>

              {/* Income vs Expenses Chart */}
              <div id="home-chart" className="chart-card">
                <div className="chart-card-header">
                  <div>
                    <h3 className="chart-card-title">Income vs Expenses</h3>
                    <p className="chart-card-subtitle">Monthly comparison</p>
                  </div>
                </div>
                <div className="chart-body">
                  {renderBarChart(monthlyIncomeExpense)}
                </div>
                <div className="chart-legend-row">
                  <div className="legend-item">
                    <span className="legend-dot legend-dot-green" />
                    Income
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot legend-dot-red" />
                    Expenses
                  </div>
                </div>
              </div>
            </div>

            {/* ======= RIGHT PANEL ======= */}
            <div className="dashboard-right">

              {/* Reminders carousel */}
              <div id="home-reminders" className="right-card right-todo-card">
                <div className="right-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 className="right-card-title">Reminders</h3>
                    {todos.length > 0 && <span className="todo-count-pill">{todos.length}</span>}
                  </div>
                  <button className="add-reminder-btn" onClick={() => setShowAddReminder(true)} title="Add Reminder">
                    <FaPlus />
                  </button>
                </div>

                {todos.length > 0 ? (() => {
                  const sortedTodos = [...todos].sort((a, b) => getTodoUrgency(a) - getTodoUrgency(b));
                  const todo = sortedTodos[todoPage] || sortedTodos[0];
                  const urgency = getTodoUrgency(todo);
                  const isOverdue = urgency <= 1;
                  const fmtDate = (d) => {
                    if (!d) return 'No due date';
                    const dt = parseLocalDate(d);
                    return !dt || isNaN(dt) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  };
                  return (
                    <>
                      <div className={`tc-card ${urgencyClass[urgency]}`}>
                        <div className="tc-top">
                          <div className="tc-title-row">
                            {isOverdue && <FaExclamationTriangle className="tc-warn-icon" />}
                            <span className="tc-title">{todo.title}</span>
                          </div>
                          <div className="tc-top-right">
                            {todo.amount && <span className="tc-amount">{todo.amount}</span>}
                            {todo.type === 'manual' && (
                              <button className="tc-delete-btn" onClick={() => { deleteTodo(todo); setTodoPage(p => Math.max(0, p - 1)); }}>
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="tc-bottom">
                          {todo.subtitle && <span className="tc-subtitle">{todo.subtitle}</span>}
                          <div className="tc-date-row">
                            <span className="tc-date">
                              {isOverdue ? 'Past Due' : todo.dueDate ? `Due ${fmtDate(todo.dueDate)}` : 'No due date'}
                            </span>
                            {isOverdue && <span className="tc-overdue-badge">· OVERDUE</span>}
                          </div>
                        </div>
                      </div>

                      <div className="todo-nav">
                        <button className="todo-nav-btn" onClick={() => setTodoPage(p => Math.max(0, p - 1))} disabled={todoPage === 0}>‹</button>
                        <div className="todo-dots">
                          {sortedTodos.slice(0, 7).map((t, i) => (
                            <span key={i} className={`todo-dot ${i === todoPage ? 'todo-dot-active' : ''}`}
                                  style={i === todoPage ? { background: ['#991b1b','#b91c1c','#dc2626','#d97706','#2563eb'][getTodoUrgency(t)] } : {}}
                                  onClick={() => setTodoPage(i)} />
                          ))}
                          {sortedTodos.length > 7 && <span className="todo-dot-more">…</span>}
                        </div>
                        <button className="todo-nav-btn" onClick={() => setTodoPage(p => Math.min(sortedTodos.length - 1, p + 1))} disabled={todoPage >= sortedTodos.length - 1}>›</button>
                      </div>
                    </>
                  );
                })() : (
                  <div className="no-todos">No reminders right now</div>
                )}
              </div>

              {/* Work Orders Card */}
              {(() => {
                const total = workOrders.length;
                const open = workOrders.filter(w => w.Stat === 'Open').length;
                const inProgress = workOrders.filter(w => w.Stat === 'In Progress').length;
                return (
                  <div id="home-work-quick" className="right-card wo-summary-card" onClick={() => setActiveModal('workorders')}>
                    <div className="right-card-header" style={{ alignItems: 'flex-start' }}>
                      <div>
                        <h3 className="right-card-title">Work Orders – MTD</h3>
                        <div className="wo-card-inprogress">{inProgress} in progress</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="wo-open-pill">{open} open</span>
                        <span className="wo-total-num">{total}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Quick Actions */}
              <div id="home-quick-actions" className="right-card">
                <h3 className="right-card-title">Quick Actions</h3>
                <div className="quick-actions-list">
                  <button className="quick-action-btn qa-expense" onClick={() => navigate('/activity', { state: { openModal: 'expense' } })}>
                    <FaPlus className="qa-icon" />
                    <span>Add Expense</span>
                  </button>
                  <button className="quick-action-btn qa-payment" onClick={() => navigate('/activity', { state: { openModal: 'income' } })}>
                    <FaPlus className="qa-icon" />
                    <span>Add Income</span>
                  </button>
                  <button className="quick-action-btn qa-projection" onClick={() => navigate('/projection')}>
                    <FaPlus className="qa-icon" />
                    <span>Run Projection</span>
                  </button>
                  <button id="home-announcement" className="quick-action-btn qa-announcement" onClick={openAnnouncementModal}>
                    <FaBullhorn className="qa-icon" />
                    <span>Send Announcement</span>
                  </button>
                </div>
              </div>

              {/* Recent Activity */}
              <div id="home-recent-activity" className="right-card">
                <div className="right-card-header">
                  <h3 className="right-card-title">Recent Activity</h3>
                  <button className="view-all-link" onClick={() => navigate('/activity')}>View All</button>
                </div>
                <div className="activity-list">
                  {recentActivity.length > 0 ? recentActivity.map((item, i) => (
                    <div key={i} className="activity-row">
                      <div className={`activity-avatar ${item.type === 'income' ? 'av-income' : 'av-expense'}`} />
                      <div className="activity-row-info">
                        <div className="activity-row-name">{item.label}</div>
                        <div className="activity-row-sub">{item.property || 'N/A'}</div>
                      </div>
                      <div className={`activity-row-amount ${item.type === 'income' ? 'amt-income' : 'amt-expense'}`}>
                        {item.type === 'income' ? '+' : '-'}${fmtShort(Math.abs(item.amount))}
                      </div>
                    </div>
                  )) : (
                    <div className="no-activity">No recent activity</div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ======= MODALS ======= */}
      {activeModal && (
        <div className="modal-overlay" onClick={() => setActiveModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {activeModal === 'income' && 'YTD Income Summary'}
                {activeModal === 'cashflow' && 'YTD Cash Flow Summary'}
                {activeModal === 'expense' && 'Expense Ratio Breakdown'}
                {activeModal === 'occupancy' && 'Occupancy Summary'}
                {activeModal === 'workorders' && 'Work Orders'}
              </h2>
              <button className="modal-close-btn" onClick={() => setActiveModal(null)}>✕</button>
            </div>

            <div className="modal-body">

              {/* ---- INCOME MODAL ---- */}
              {activeModal === 'income' && (
                <>
                  <div className="modal-total-box modal-total-green" style={{ marginTop: 0, marginBottom: 16 }}>
                    <div className="modal-total-lbl">TOTAL YTD INCOME</div>
                    <div className="modal-total-val">${fmt(ytdIncome)}</div>
                  </div>

                  <button className="modal-collapse-btn" onClick={() => setIncomeMonthOpen(o => !o)}>
                    <span>Income by Month</span>
                    {incomeMonthOpen ? <FaChevronUp className="modal-collapse-icon" /> : <FaChevronDown className="modal-collapse-icon" />}
                  </button>
                  {incomeMonthOpen && (
                    incomeByMonth.length > 0 ? (
                      incomeByMonth.map((m, i) => (
                        <div key={i} className={`modal-row ${i % 2 === 0 ? 'modal-row-stripe' : ''}`}>
                          <span className="modal-row-text">{m.month}</span>
                          <span className="modal-val-green">${fmtShort(m.total)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-activity">No income data</div>
                    )
                  )}

                  <button className="modal-collapse-btn" style={{ marginTop: 10 }} onClick={() => setIncomePropOpen(o => !o)}>
                    <span>Income by Property</span>
                    {incomePropOpen ? <FaChevronUp className="modal-collapse-icon" /> : <FaChevronDown className="modal-collapse-icon" />}
                  </button>
                  {incomePropOpen && (
                    incomeByProperty.length > 0 ? (
                      incomeByProperty.map((p, i) => (
                        <div key={i} className={`modal-row ${i % 2 === 0 ? 'modal-row-stripe' : ''}`}>
                          <span className="modal-row-link">{p.property}</span>
                          <span className="modal-val-green">${fmtShort(p.total)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-activity">No property income</div>
                    )
                  )}
                </>
              )}

              {/* ---- CASH FLOW MODAL ---- */}
              {activeModal === 'cashflow' && (
                <>
                  <div className={`modal-total-box ${modalNetCashFlow >= 0 ? 'modal-total-green' : 'modal-total-red'}`} style={{ marginTop: 0, marginBottom: 16 }}>
                    <div className="modal-total-lbl">TOTAL YTD CASH FLOW</div>
                    <div className="modal-total-val" style={{ color: modalNetCashFlow >= 0 ? '#15803d' : '#dc2626' }}>
                      {modalNetCashFlow < 0 ? '-' : ''}${fmt(modalNetCashFlow)}
                    </div>
                  </div>

                  <div className="modal-2col">
                    <div className="modal-hl modal-hl-green">
                      <div className="modal-hl-label">BEST MONTH</div>
                      <div className="modal-hl-main" style={{ color: '#16a34a', fontSize: 18 }}>{modalBestMonth ? stripYear(modalBestMonth.label) : '—'}</div>
                      <div className="modal-hl-sub" style={{ color: (modalBestMonth?.value || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                        {(modalBestMonth?.value || 0) < 0 ? '-' : ''}${fmt(modalBestMonth?.value || 0)}
                      </div>
                    </div>
                    <div className="modal-hl modal-hl-red">
                      <div className="modal-hl-label" style={{ color: '#dc2626' }}>WORST MONTH</div>
                      <div className="modal-hl-main" style={{ color: '#dc2626', fontSize: 18 }}>{modalWorstMonth ? stripYear(modalWorstMonth.label) : '—'}</div>
                      <div className="modal-hl-sub" style={{ color: (modalWorstMonth?.value || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                        {(modalWorstMonth?.value || 0) < 0 ? '-' : ''}${fmt(modalWorstMonth?.value || 0)}
                      </div>
                    </div>
                  </div>

                  <div className="modal-section-label">Monthly Cash Flow</div>
                  {safeCashFlow.map((m, i) => {
                    const label = m?.label ?? '—';
                    const value = Number(m?.value ?? 0);
                    return (
                      <div key={i} className={`modal-row ${i % 2 === 0 ? 'modal-row-stripe' : ''}`}>
                        <span className="modal-row-text">{stripYear(label)}</span>
                        <span className={value >= 0 ? 'modal-val-green' : 'modal-val-red'}>
                          {value < 0 ? '-' : ''}${fmtShort(value)}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}

              {/* ---- EXPENSE RATIO MODAL ---- */}
              {activeModal === 'expense' && (
                <>
                  <div className="modal-2col">
                    <div className="modal-hl modal-hl-green">
                      <div className="modal-hl-label">TOTAL INCOME</div>
                      <div className="modal-hl-main" style={{ color: '#16a34a' }}>
                        ${fmt(totalIncome.current)}
                      </div>
                    </div>

                    <div className="modal-hl modal-hl-red">
                      <div className="modal-hl-label" style={{ color: '#dc2626' }}>
                        TOTAL EXPENSES
                      </div>
                      <div className="modal-hl-main" style={{ color: '#dc2626' }}>
                        ${fmt(totalExpenses.current)}
                      </div>
                    </div>
                  </div>

                  <div className="modal-hl modal-hl-blue" style={{ marginBottom: 16 }}>
                    <div className="modal-hl-label" style={{ color: '#2563eb' }}>EXPENSE RATIO</div>
                    <div className="modal-hl-main" style={{ color: '#2563eb', fontSize: 36 }}>{expenseRatio}%</div>
                    <div className="modal-hl-sub" style={{ color: '#93c5fd' }}>Expenses / Income</div>
                  </div>

                  <div className="modal-section-label">Expenses by Category</div>
                  {expenseCategoryList.length > 0 ? expenseCategoryList.map((e, i) => (
                    <div key={i} className={`modal-row ${i % 2 === 0 ? 'modal-row-stripe' : ''}`}>
                      <span className="modal-row-text">{e.cat}</span>
                      <span className="modal-val-red">${fmtShort(e.total)}</span>
                    </div>
                  )) : <div className="no-activity">No expenses</div>}
                </>
              )}

              {/* ---- WORK ORDERS MODAL ---- */}
              {activeModal === 'workorders' && (
                (() => {
                  const openWOs = workOrders.filter(w => w.Stat === 'Open');
                  const inProgressWOs = workOrders.filter(w => w.Stat === 'In Progress');
                  const priorityInfo = (p) => {
                    if (p === 1) return { label: 'Urgent', bg: '#fef2f2', color: '#dc2626' };
                    if (p === 2) return { label: 'Medium', bg: '#fff7ed', color: '#ea580c' };
                    return { label: 'Normal', bg: '#f0fdf4', color: '#16a34a' };
                  };
                  return (
                    <>
                      <div className="modal-3col">
                        <div className="modal-hl modal-hl-blue">
                          <div className="modal-hl-label" style={{ color: '#2563eb' }}>TOTAL</div>
                          <div className="modal-hl-main" style={{ color: '#2563eb' }}>{workOrders.length}</div>
                        </div>
                        <div className="modal-hl" style={{ background: '#fef2f2' }}>
                          <div className="modal-hl-label" style={{ color: '#dc2626' }}>OPEN</div>
                          <div className="modal-hl-main" style={{ color: '#dc2626' }}>{openWOs.length}</div>
                        </div>
                        <div className="modal-hl" style={{ background: '#fff7ed' }}>
                          <div className="modal-hl-label" style={{ color: '#ea580c' }}>IN PROGRESS</div>
                          <div className="modal-hl-main" style={{ color: '#ea580c' }}>{inProgressWOs.length}</div>
                        </div>
                      </div>

                      <div className="modal-section-label">Open Work Orders</div>
                      {openWOs.length > 0 ? openWOs.map((w, i) => {
                        const prio = priorityInfo(w.Priority);
                        return (
                          <div key={i} className={`modal-row ${i % 2 === 0 ? 'modal-row-stripe' : ''}`} style={{ alignItems: 'flex-start', padding: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                              <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#dc2626', marginTop: 4, flexShrink: 0 }} />
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{w.Title || 'Untitled'}</div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{w.Property_Name || '—'}</div>
                              </div>
                            </div>
                            <span style={{ background: prio.bg, color: prio.color, fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px', flexShrink: 0 }}>
                              {prio.label}
                            </span>
                          </div>
                        );
                      }) : <div className="no-activity">No open work orders</div>}

                      <div style={{ marginTop: 16, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, textAlign: 'center', fontSize: 13, color: '#2563eb', fontWeight: 500, cursor: 'pointer' }}
                           onClick={() => { setActiveModal(null); navigate('/work-orders'); }}>
                        Go to the Work Orders tab for more details
                      </div>
                    </>
                  );
                })()
              )}

              {/* ---- OCCUPANCY MODAL ---- */}
              {activeModal === 'occupancy' && (
                <>
                  <div className="modal-3col">
                    <div className="modal-hl modal-hl-blue">
                      <div className="modal-hl-label" style={{ color: '#2563eb' }}>TOTAL UNITS</div>
                      <div className="modal-hl-main" style={{ color: '#2563eb' }}>{propertyStats.units}</div>
                    </div>
                    <div className="modal-hl modal-hl-green">
                      <div className="modal-hl-label">OCCUPIED</div>
                      <div className="modal-hl-main" style={{ color: '#16a34a' }}>{propertyStats.occupied}</div>
                    </div>
                    <div className="modal-hl modal-hl-orange">
                      <div className="modal-hl-label" style={{ color: '#ea580c' }}>VACANT</div>
                      <div className="modal-hl-main" style={{ color: '#ea580c' }}>{propertyStats.vacant}</div>
                    </div>
                  </div>

                  <div className="modal-section-label">Vacant Units Available</div>
                  {vacantUnitsList.length > 0 ? vacantUnitsList.map((u, i) => (
                    <div key={i} className="modal-vacant-card">
                      <div className="modal-vacant-top">
                        <span className="modal-vacant-name">{u.name}{u.unit ? ` - Unit ${u.unit}` : ''}</span>
                        <span className="modal-vacant-badge">VACANT</span>
                      </div>
                      <div className="modal-vacant-addr" style={{ fontSize: 14 }}>{[u.address, u.city, u.state, u.zip].filter(Boolean).join(', ')}</div>
                    </div>
                  )) : <div className="no-activity">No vacant units</div>}
                </>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ======= ADD REMINDER MODAL ======= */}
      {showAddReminder && (() => {
        const previewUrgency = reminderDueDate
          ? (() => {
              const days = Math.ceil((new Date(reminderDueDate) - new Date()) / (1000 * 60 * 60 * 24));
              if (days < 0) return 0;
              if (days <= 3) return 1;
              return 2;
            })()
          : 3;
        const gradients = [
          'linear-gradient(135deg, #7f1d1d, #dc2626)',
          'linear-gradient(135deg, #dc2626, #f87171)',
          'linear-gradient(135deg, #c2410c, #fb923c)',
          'linear-gradient(135deg, #1d4ed8, #60a5fa)',
        ];
        const btnColors = ['#991b1b', '#dc2626', '#c2410c', '#1d4ed8'];
        return (
          <div className="modal-overlay" onClick={() => { setShowAddReminder(false); setReminderText(''); setReminderDueDate(''); }}>
            <div className="add-reminder-modal" onClick={e => e.stopPropagation()}>
              <div className="add-reminder-modal-header" style={{ background: gradients[previewUrgency] }}>
                <span className="add-reminder-modal-title">New Reminder</span>
                <button className="modal-close-btn" style={{ color: '#fff', opacity: 0.85 }}
                        onClick={() => { setShowAddReminder(false); setReminderText(''); setReminderDueDate(''); }}>✕</button>
              </div>
              <div className="add-reminder-modal-body">
                <label className="add-reminder-label">Reminder</label>
                <textarea
                  className="add-reminder-textarea"
                  placeholder="What do you need to remember?"
                  value={reminderText}
                  onChange={e => setReminderText(e.target.value)}
                  rows={3}
                  autoFocus
                />
                <label className="add-reminder-label" style={{ marginTop: 12 }}>Due Date <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                <input
                  type="date"
                  className="add-reminder-date"
                  value={reminderDueDate}
                  onChange={e => setReminderDueDate(e.target.value)}
                />
                {reminderDueDate && (
                  <div className="add-reminder-preview" style={{ background: gradients[previewUrgency] }}>
                    {['Overdue', 'Due very soon', 'Upcoming', 'No due date'][previewUrgency]}
                  </div>
                )}
                <div className="add-reminder-actions">
                  <button className="add-reminder-cancel" onClick={() => { setShowAddReminder(false); setReminderText(''); setReminderDueDate(''); }}>
                    Cancel
                  </button>
                  <button
                    className="add-reminder-submit"
                    style={{ background: btnColors[previewUrgency] }}
                    onClick={addReminder}
                    disabled={!reminderText.trim() || reminderSubmitting}
                  >
                    {reminderSubmitting ? 'Saving…' : 'Add Reminder'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* ======= ANNOUNCEMENT MODAL ======= */}
      {showAnnouncement && (() => {
        const canSubmit = annTitle.trim() && annMessage.trim() && annSelected.size > 0 && !annSubmitting;
        return (
          <div className="modal-overlay" onClick={() => setShowAnnouncement(false)}>
            <div className="ann-modal" onClick={e => e.stopPropagation()}>

              {/* Purple gradient header */}
              <div className="ann-modal-header">
                <div className="ann-hd-left">
                  <div className="ann-hd-icon-wrap"><FaBullhorn /></div>
                  <div>
                    <div className="ann-modal-title">New Announcement</div>
                    <div className="ann-modal-subtitle">Notify tenants across your properties</div>
                  </div>
                </div>
                <button className="ann-hd-close" onClick={() => setShowAnnouncement(false)}>✕</button>
              </div>

              {annSuccess ? (
                <div className="ann-success">
                  <div className="ann-success-icon">✓</div>
                  <div className="ann-success-text">Announcement sent!</div>
                </div>
              ) : (
                <div className="ann-modal-body">
                  <label className="ann-label">TITLE <span className="ann-req">*</span></label>
                  <input
                    className="ann-input"
                    placeholder="e.g. Water shut-off Saturday 9–11 AM"
                    value={annTitle}
                    onChange={e => setAnnTitle(e.target.value)}
                    maxLength={120}
                    autoFocus
                  />

                  <label className="ann-label" style={{ marginTop: 14 }}>MESSAGE <span className="ann-req">*</span></label>
                  <textarea
                    className="ann-textarea"
                    placeholder="Write your announcement here…"
                    value={annMessage}
                    onChange={e => setAnnMessage(e.target.value)}
                    rows={4}
                  />

                  <label className="ann-label" style={{ marginTop: 14 }}>EXPIRES AFTER</label>
                  <div className="ann-exp-pills">
                    {[7, 14, 30, 60, 90].map(d => (
                      <button
                        key={d}
                        className={`ann-pill ${annExpDays === d ? 'ann-pill-active' : ''}`}
                        onClick={() => setAnnExpDays(d)}
                      >
                        {d} days
                      </button>
                    ))}
                  </div>

                  <label className="ann-label" style={{ marginTop: 14 }}>
                    RECIPIENTS <span className="ann-req">*</span>
                    {annSelected.size > 0 && <span className="ann-sel-count"> · {annSelected.size} selected</span>}
                  </label>

                  {annDoorsLoading ? (
                    <div className="ann-loading">Loading properties…</div>
                  ) : annDoors.length === 0 ? (
                    <div className="ann-loading">No units found</div>
                  ) : (
                    <div className="ann-recipients">
                      {(() => {
                        const groups = {};
                        annDoors.forEach(d => {
                          if (!groups[d.property_name]) groups[d.property_name] = [];
                          groups[d.property_name].push(d);
                        });
                        return Object.entries(groups).map(([propName, doors]) => {
                          if (doors.length === 1) {
                            const door = doors[0];
                            return (
                              <label key={door.door_id} className="ann-flat-row">
                                <input type="checkbox" className="ann-checkbox"
                                  checked={annSelected.has(door.door_id)}
                                  onChange={() => toggleAnnDoor(door.door_id)} />
                                <span className="ann-flat-name">{propName}</span>
                              </label>
                            );
                          }
                          const allSel = doors.every(d => annSelected.has(d.door_id));
                          const someSel = doors.some(d => annSelected.has(d.door_id));
                          return (
                            <div key={propName} className="ann-group">
                              <label className="ann-group-hd">
                                <input type="checkbox" className="ann-checkbox"
                                  checked={allSel}
                                  ref={el => { if (el) el.indeterminate = someSel && !allSel; }}
                                  onChange={() => toggleAnnProperty(doors)} />
                                <span className="ann-flat-name">{propName}</span>
                                <span className="ann-unit-count">{doors.length} units</span>
                              </label>
                              <div className="ann-units">
                                {doors.map(door => (
                                  <label key={door.door_id} className="ann-unit-row">
                                    <input type="checkbox" className="ann-checkbox"
                                      checked={annSelected.has(door.door_id)}
                                      onChange={() => toggleAnnDoor(door.door_id)} />
                                    <span className="ann-unit-name">Unit {door.unit_number}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                  {annError && (
                    <p style={{ color: '#ef4444', fontSize: 13, margin: '8px 0 0', textAlign: 'center' }}>{annError}</p>
                  )}

                  <div className="ann-actions">
                    <button className="ann-cancel" onClick={() => setShowAnnouncement(false)}>Cancel</button>
                    <button className="ann-submit" onClick={submitAnnouncement} disabled={!canSubmit}>
                      {annSubmitting ? 'Sending…' : annSelected.size > 0 ? `Send to ${annSelected.size}` : 'Send'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Tutorial — only mounts after onboarding wizard closes */}
      {userEmail && onboardStep === 0 && (
        <Tutorial
          steps={HOME_STEPS}
          storageKey={`hasSeenHomeTutorial_${userEmail}`}
          onStep={(stepIdx) => {
            if (stepIdx === -1) {
              const wasDemo = localStorage.getItem(`isDemoMode_${userEmail}`) === 'true';
              localStorage.removeItem(`isDemoMode_${userEmail}`);
              setDemoMode(false);
              if (wasDemo) {
                setOccupancyData({ occupied: 0, total: 0 });
                setPropertyStats({ total: 0, units: 0, vacant: 0, occupied: 0 });
                setTotalIncome({ current: 0, lastMonth: 0 });
                setTotalExpenses({ current: 0, lastMonth: 0 });
                setCashFlowData([]);
                setMonthlyIncomeExpense([]);
                setYtdIncome(0);
                setYtdExpenses(0);
                setYtdCashFlow(0);
                setRecentActivity([]);
              }
            }
          }}
        />
      )}

      {/* ======= ONBOARDING WIZARD ======= */}
      {onboardStep > 0 && (
        <div className="ob-overlay">
          <div className="ob-modal" onClick={e => e.stopPropagation()}>

            {/* STEP 1 — Bank question */}
            {onboardStep === 1 && (
              <>
                <div className="ob-icon">🏦</div>
                <h2 className="ob-title">Connect Your Bank</h2>
                <p className="ob-desc">Automatically import transactions to track income and expenses — no manual entry needed.</p>
                <div className="ob-actions">
                  <button className="ob-later" onClick={() => setOnboardStep(3)}>Later</button>
                  <button className="ob-yes" onClick={() => setOnboardStep(2)}>Yes, Connect →</button>
                </div>
              </>
            )}

            {/* STEP 2 — Bank connect UI */}
            {onboardStep === 2 && (
              <>
                <div className="ob-icon">🏦</div>
                <h2 className="ob-title">Connect Your Bank</h2>
                <p className="ob-desc">Securely link your bank via Plaid. Your credentials are never stored by us.</p>
                {obBankStatus === 'success' ? (
                  <>
                    <p className="ob-success">✓ Bank connected and transactions synced!</p>
                    <button className="ob-yes" onClick={() => setOnboardStep(3)}>Next →</button>
                  </>
                ) : (
                  <>
                    {obBankMsg && <p className="ob-error">{obBankMsg}</p>}
                    <div className="ob-actions">
                      <button className="ob-later" onClick={() => setOnboardStep(3)}>Skip</button>
                      <button className="ob-yes" onClick={handleOnboardBank} disabled={obBankConnecting}>
                        {obBankConnecting ? 'Connecting…' : 'Connect via Plaid'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {/* STEP 3 — Payment question */}
            {onboardStep === 3 && (
              <>
                <div className="ob-icon">💳</div>
                <h2 className="ob-title">Accept Online Payments</h2>
                <p className="ob-desc">Connect Stripe to let tenants pay rent directly through the app — fast, secure, and automatic.</p>
                <div className="ob-actions">
                  <button className="ob-later" onClick={finishOnboarding}>Later</button>
                  <button className="ob-yes" onClick={() => { setObStripeOpened(false); setOnboardStep(4); }}>Yes, Connect →</button>
                </div>
              </>
            )}

            {/* STEP 4 — Stripe connect UI */}
            {onboardStep === 4 && (
              <>
                <div className="ob-icon">💳</div>
                <h2 className="ob-title">Connect Stripe</h2>
                <p className="ob-desc">Click below to open Stripe's secure onboarding in a new tab. Complete the setup there, then come back here.</p>
                {obStripeOpened ? (
                  <>
                    <p className="ob-success">✓ Stripe tab opened — finish setup there, then continue.</p>
                    <button className="ob-yes" onClick={finishOnboarding}>Done →</button>
                  </>
                ) : (
                  <div className="ob-actions">
                    <button className="ob-later" onClick={finishOnboarding}>Skip</button>
                    <button className="ob-yes" onClick={handleOnboardStripe} disabled={obStripeLoading}>
                      {obStripeLoading ? 'Loading…' : 'Connect via Stripe →'}
                    </button>
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
